import { useState, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicLandingAiBubble from '@/components/PublicLandingAiBubble';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  MapPin, BedDouble, Bath, Car, Ruler, Phone, Mail, Send,
  ChevronLeft, ChevronRight, X, Calendar, Building2, Home,
  ArrowRight, Loader2, CheckCircle2, Heart
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { normalizePhone } from '@/lib/phoneUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const OP_LABELS = { venta: 'Venta', renta: 'Renta', preventa: 'Preventa' };
const TYPE_LABELS = { casa: 'Casa', departamento: 'Depto', terreno: 'Terreno', oficina: 'Oficina', local: 'Local', bodega: 'Bodega' };

function formatPrice(price, currency = 'MXN') {
  if (!price) return '';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
}

function PropertyCard({ property, accentColor, onSelect, isDark }) {
  const img = property.images?.[0];
  const imgUrl = img ? (img.startsWith('http') ? img : `${API}${img}`) : null;

  return (
    <div
      className={`group cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border ${isDark ? 'bg-[#1E293B] border-white/5' : 'bg-white border-gray-100'}`}
      onClick={() => onSelect(property)}
      data-testid={`property-card-${property.id}`}
    >
      <div className={`relative aspect-[4/3] overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
        {imgUrl ? (
          <img src={imgUrl} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><Home className="w-12 h-12" /></div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <Badge className="text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: accentColor }}>{OP_LABELS[property.operation_type] || property.operation_type}</Badge>
          <Badge className="bg-black/60 text-white text-[10px] backdrop-blur-sm">{TYPE_LABELS[property.property_type] || property.property_type}</Badge>
        </div>
        <div className="absolute bottom-3 right-3">
          <span className="text-white font-bold text-lg drop-shadow-lg bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-lg">{formatPrice(property.price, property.currency)}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className={`font-semibold text-sm mb-1 line-clamp-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{property.title}</h3>
        {(property.city || property.address) && (
          <p className={`text-xs flex items-center gap-1 mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}><MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{property.city}{property.state ? `, ${property.state}` : ''}</span></p>
        )}
        <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          {property.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{property.bedrooms}</span>}
          {property.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{property.bathrooms}</span>}
          {property.parking_spots > 0 && <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" />{property.parking_spots}</span>}
          {property.area_m2 > 0 && <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" />{property.area_m2}m2</span>}
        </div>
      </div>
    </div>
  );
}

function PropertyModal({ property, company, accentColor, onClose, onContact }) {
  const [imgIdx, setImgIdx] = useState(0);
  const images = property.images || [];

  const getImgUrl = (img) => img ? (img.startsWith('http') ? img : `${API}${img}`) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="property-modal">
        {/* Image Gallery */}
        <div className="relative aspect-video bg-gray-100">
          {images.length > 0 ? (
            <img src={getImgUrl(images[imgIdx])} alt={property.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300"><Home className="w-16 h-16" /></div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/60"><X className="w-5 h-5" /></button>
          {images.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/60"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={e => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/60"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === imgIdx ? 'bg-white scale-125' : 'bg-white/50'}`} />)}
              </div>
            </>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{property.title}</h2>
              {(property.city || property.address) && (
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{property.address}{property.city ? `, ${property.city}` : ''}{property.state ? `, ${property.state}` : ''}</p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold" style={{ color: accentColor }}>{formatPrice(property.price, property.currency)}</p>
              <Badge className="text-white text-xs mt-1" style={{ backgroundColor: accentColor }}>{OP_LABELS[property.operation_type] || property.operation_type}</Badge>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 p-3 rounded-xl bg-gray-50 mb-4">
            {property.bedrooms > 0 && <div className="text-center"><BedDouble className="w-5 h-5 mx-auto text-gray-500" /><p className="text-sm font-semibold mt-1">{property.bedrooms}</p><p className="text-[10px] text-gray-500">Recamaras</p></div>}
            {property.bathrooms > 0 && <div className="text-center"><Bath className="w-5 h-5 mx-auto text-gray-500" /><p className="text-sm font-semibold mt-1">{property.bathrooms}</p><p className="text-[10px] text-gray-500">Banos</p></div>}
            {property.parking_spots > 0 && <div className="text-center"><Car className="w-5 h-5 mx-auto text-gray-500" /><p className="text-sm font-semibold mt-1">{property.parking_spots}</p><p className="text-[10px] text-gray-500">Estac.</p></div>}
            {property.area_m2 > 0 && <div className="text-center"><Ruler className="w-5 h-5 mx-auto text-gray-500" /><p className="text-sm font-semibold mt-1">{property.area_m2}</p><p className="text-[10px] text-gray-500">m2</p></div>}
          </div>

          {property.description && <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{property.description}</p>}

          {property.amenities?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Amenidades</p>
              <div className="flex flex-wrap gap-1.5">
                {property.amenities.map(a => <Badge key={a} variant="outline" className="text-[10px] border-gray-200 text-gray-600">{a}</Badge>)}
              </div>
            </div>
          )}

          <Button className="w-full text-white" style={{ backgroundColor: accentColor }} onClick={() => onContact(property)} data-testid="modal-contact-btn">
            <Mail className="w-4 h-4 mr-2" /> Me interesa esta propiedad
          </Button>
        </div>
      </div>
    </div>
  );
}

function ContactForm({ company, accentColor, propertyId, abVariantId, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Ingresa tu nombre'); return; }
    if (!form.email && !form.phone) { toast.error('Ingresa email o telefono'); return; }
    setSending(true);
    try {
      const normalizedPhone = form.phone ? normalizePhone(form.phone, '52', 'default') : '';
      const r = await fetch(`${API}/api/agency/${company.slug}/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, phone: normalizedPhone, property_id: propertyId || '', ab_variant_id: abVariantId || '' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Error');
      toast.success(data.message || 'Enviado');
      setForm({ name: '', email: '', phone: '', message: '' });
      onSuccess?.();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
      <Input placeholder="Nombre *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white border-gray-200" data-testid="contact-name" />
      <div className="grid grid-cols-2 gap-3">
        <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white border-gray-200" data-testid="contact-email" />
        <Input type="tel" placeholder="Telefono" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white border-gray-200" data-testid="contact-phone" />
      </div>
      <Textarea placeholder="Mensaje (opcional)" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className="bg-white border-gray-200 resize-none" data-testid="contact-message" />
      <Button type="submit" disabled={sending} className="w-full text-white" style={{ backgroundColor: accentColor }} data-testid="contact-submit">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        {sending ? 'Enviando...' : 'Enviar mensaje'}
      </Button>
    </form>
  );
}

export default function AgencyPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProp, setSelectedProp] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [contactPropId, setContactPropId] = useState('');
  const [filter, setFilter] = useState({ type: '', operation: '' });

  useEffect(() => {
    fetch(`${API}/api/agency/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4" data-testid="agency-not-found">
      <Building2 className="w-16 h-16 text-gray-300 mb-4" />
      <h1 className="text-xl font-bold text-gray-700 mb-2">Agencia no encontrada</h1>
      <p className="text-sm text-gray-500">El enlace puede estar incorrecto o la pagina no esta disponible.</p>
    </div>
  );

  const { company, properties, calendly_links } = data;
  const abVariant = data.ab_variant;
  const waBubblePhone = data.wa_bubble_phone;
  const accent = company.primary_color || '#2563EB';
  const secondary = company.secondary_color || '#1B5E7B';
  const tertiary = company.tertiary_color || '#0B0C10';
  const template = company.landing_template || 'classic';
  const logoUrl = company.logo_url ? (company.logo_url.startsWith('http') ? company.logo_url : `${API}${company.logo_url}`) : null;

  const isDark = template === 'modern_dark';
  const bgMain = isDark ? tertiary : (template === 'corporate' ? '#F0F4F8' : '#FAFAFA');
  const textMain = isDark ? '#F8FAFC' : '#1F2937';
  const textSub = isDark ? '#94A3B8' : '#6B7280';
  const cardBg = isDark ? '#1E293B' : '#FFFFFF';
  const headerBg = isDark ? `${tertiary}E6` : 'rgba(255,255,255,0.8)';
  const footerBg = isDark ? '#020617' : '#111827';

  const filteredProps = properties.filter(p => {
    if (filter.type && p.property_type !== filter.type) return false;
    if (filter.operation && p.operation_type !== filter.operation) return false;
    return true;
  });

  const propTypes = [...new Set(properties.map(p => p.property_type))];
  const opTypes = [...new Set(properties.map(p => p.operation_type))];

  const openContact = (propId = '') => { setContactPropId(propId); setShowContact(true); setSelectedProp(null); };

  return (
    <div className="min-h-screen" style={{ background: bgMain, color: textMain }} data-testid="agency-landing" data-template={template}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-lg border-b" style={{ background: headerBg, borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' }} data-testid="agency-header">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={company.name} className="h-14 object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <Building2 className="w-6 h-6" style={{ color: accent }} />
                <span className="font-bold" style={{ color: textMain }}>{company.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {company.contact_phone && (
              <a href={`tel:${company.contact_phone}`} className="hidden sm:flex items-center gap-1.5 text-xs" style={{ color: textSub }}>
                <Phone className="w-3.5 h-3.5" />{company.contact_phone}
              </a>
            )}
            <Button size="sm" className="text-white text-xs" style={{ backgroundColor: accent }} onClick={() => openContact()} data-testid="header-contact-btn">
              Contactar
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: isDark ? `linear-gradient(135deg, ${accent}20 0%, ${tertiary} 100%)` : `linear-gradient(135deg, ${accent}15 0%, ${accent}05 100%)` }} data-testid="agency-hero">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4" style={{ color: textMain }}>
              {abVariant?.headline || company.landing_headline || `Encuentra tu propiedad ideal con ${company.name}`}
            </h1>
            <p className="text-base sm:text-lg mb-8 leading-relaxed" style={{ color: textSub }}>
              {abVariant?.description || company.landing_description || company.description || 'Explora nuestro catalogo de propiedades y encuentra la que se adapte a tus necesidades.'}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="text-white shadow-lg" style={{ backgroundColor: accent }} onClick={() => document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-cta">
                {abVariant?.cta_text || 'Ver propiedades'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {calendly_links?.length > 0 && (
                <a href={calendly_links[0].scheduling_url} target="_blank" rel="noreferrer">
                  <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="hero-calendly-btn">
                    <Calendar className="w-4 h-4 mr-2" /> Agendar visita
                  </Button>
                </a>
              )}
            </div>
          </div>
          <div className="mt-8 flex gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: accent }}>{properties.length}</p>
              <p className="text-xs text-gray-500">Propiedades</p>
            </div>
            {properties.filter(p => p.operation_type === 'venta').length > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{properties.filter(p => p.operation_type === 'venta').length}</p>
                <p className="text-xs text-gray-500">En venta</p>
              </div>
            )}
            {properties.filter(p => p.operation_type === 'renta').length > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{properties.filter(p => p.operation_type === 'renta').length}</p>
                <p className="text-xs text-gray-500">En renta</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Properties */}
      <section id="properties" className="max-w-6xl mx-auto px-4 py-12" data-testid="agency-properties-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h2 className="text-xl font-bold" style={{ color: textMain }}>Propiedades disponibles</h2>
          <div className="flex gap-2 flex-wrap">
            {opTypes.length > 1 && opTypes.map(op => (
              <button
                key={op}
                onClick={() => setFilter(f => ({ ...f, operation: f.operation === op ? '' : op }))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter.operation === op ? 'text-white shadow-sm' : ''}`}
                style={filter.operation === op ? { backgroundColor: accent } : { background: isDark ? '#1E293B' : '#F3F4F6', color: textSub }}
              >{OP_LABELS[op] || op}</button>
            ))}
            {propTypes.length > 1 && propTypes.map(t => (
              <button
                key={t}
                onClick={() => setFilter(f => ({ ...f, type: f.type === t ? '' : t }))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter.type === t ? 'text-white shadow-sm' : ''}`}
                style={filter.type === t ? { backgroundColor: accent } : { background: isDark ? '#1E293B' : '#F3F4F6', color: textSub }}
              >{TYPE_LABELS[t] || t}</button>
            ))}
          </div>
        </div>

        {filteredProps.length === 0 ? (
          <div className="text-center py-16">
            <Home className="w-12 h-12 mx-auto mb-3" style={{ color: isDark ? '#475569' : '#D1D5DB' }} />
            <p className="text-sm" style={{ color: textSub }}>No hay propiedades con estos filtros.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
            {filteredProps.map(p => (
              <PropertyCard key={p.id} property={p} accentColor={accent} onSelect={setSelectedProp} isDark={isDark} />
            ))}
          </div>
        )}
      </section>

      {/* Contact Section */}
      <section className="py-12 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', background: isDark ? `linear-gradient(135deg, ${accent}10 0%, transparent 100%)` : `linear-gradient(135deg, ${accent}08 0%, transparent 100%)` }} data-testid="agency-contact-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center mb-2" style={{ color: textMain }}>Contactanos</h2>
            <p className="text-sm text-center mb-6" style={{ color: textSub }}>Dejanos tus datos y un asesor se pondra en contacto contigo.</p>
            <ContactForm company={company} accentColor={accent} abVariantId={abVariant?.id} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: footerBg }} className="text-gray-400 py-8" data-testid="agency-footer">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm">{company.name}</p>
          {company.address && <p className="text-xs mt-1">{company.address}</p>}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            {company.contact_email && <a href={`mailto:${company.contact_email}`} className="hover:text-white flex items-center gap-1"><Mail className="w-3 h-3" />{company.contact_email}</a>}
            {company.contact_phone && <a href={`tel:${company.contact_phone}`} className="hover:text-white flex items-center gap-1"><Phone className="w-3 h-3" />{company.contact_phone}</a>}
          </div>
          <p className="text-[10px] mt-6 text-gray-600">Powered by {APP_NAME}</p>
        </div>
      </footer>

      {/* WhatsApp Floating Bubble */}
      {waBubblePhone && (
        <a
          href={`https://wa.me/${waBubblePhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola! Me interesa conocer mas sobre sus propiedades en ${company.name}`)}`}
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 group"
          data-testid="wa-bubble"
          title="Chatea con nosotros por WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[8px] text-white font-bold flex items-center justify-center animate-pulse">1</span>
        </a>
      )}
      <PublicLandingAiBubble endpoint={`${API}/api/agency/${company.slug}/chat`} intro={`Hola, soy la IA de ${company.name}. Puedo ayudarte con sus propiedades activas.`} />

      {/* Property Modal */}
      {selectedProp && <PropertyModal property={selectedProp} company={company} accentColor={accent} onClose={() => setSelectedProp(null)} onContact={(p) => openContact(p.id)} />}

      {/* Floating Contact Modal */}
      {showContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowContact(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Contactar a {company.name}</h3>
              <button onClick={() => setShowContact(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <ContactForm company={company} accentColor={accent} propertyId={contactPropId} abVariantId={abVariant?.id} onSuccess={() => setShowContact(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
